import {
  createPushSubscription,
  unsubscribePushEndpoint,
} from "../api/pushNotifications";

function base64UrlToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

function detectPlatform() {
  return (
    navigator.userAgentData?.platform ||
    navigator.platform ||
    "Unknown platform"
  ).slice(0, 80);
}

function detectDeviceName() {
  const platform = detectPlatform();
  const browser = navigator.userAgentData?.brands?.[0]?.brand || "Browser";
  return `${browser} on ${platform}`.slice(0, 120);
}

export function getPushCapability() {
  const supported =
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent || "");
  const standalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;

  return {
    supported,
    secureContext: window.isSecureContext,
    permission: "Notification" in window ? Notification.permission : "unsupported",
    isIOS,
    standalone,
    requiresIOSInstall: isIOS && !standalone,
  };
}

export async function enablePushOnThisDevice({ publicKey, serviceWorkerPath }) {
  const capability = getPushCapability();
  if (!capability.secureContext) {
    throw new Error("Phone notifications require HTTPS, except on localhost.");
  }
  if (capability.requiresIOSInstall) {
    throw new Error(
      "On iPhone, add the dashboard to the Home Screen, open it from there, then enable notifications.",
    );
  }
  if (!capability.supported) {
    throw new Error("This browser does not support phone push notifications.");
  }
  if (!publicKey) {
    throw new Error("The server Web Push public key is missing.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  const registration = await navigator.serviceWorker.register(
    serviceWorkerPath || "/push-sw.js",
    { scope: "/" },
  );
  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(publicKey),
    });
  }

  const serialized = subscription.toJSON();
  if (!serialized.endpoint || !serialized.keys?.p256dh || !serialized.keys?.auth) {
    throw new Error("The browser returned an incomplete push subscription.");
  }

  const response = await createPushSubscription({
    endpoint: serialized.endpoint,
    expiration_time: serialized.expirationTime ?? null,
    keys: serialized.keys,
    device_name: detectDeviceName(),
    platform: detectPlatform(),
  });

  return response.data;
}

export async function disablePushOnThisDevice() {
  if (!("serviceWorker" in navigator)) return false;
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager?.getSubscription();
  if (!subscription) return false;

  try {
    await unsubscribePushEndpoint(subscription.endpoint);
  } finally {
    await subscription.unsubscribe();
  }
  return true;
}
